import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const line1 = "Your brand has";
const line2 = "a story.";
const line3 = "We make sure the";
const line4 = "world hears it.";

const easeOut = [0.22, 1, 0.36, 1];

const MaskedLine = ({ text, delay = 0, italic = false, accent = false }) => {
  return (
    <span className="block overflow-hidden pb-2">
      <motion.span
        initial={{ y: "110%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1.15, delay, ease: easeOut }}
        className={`block will-change-transform ${italic ? "font-serif-editorial italic text-[#e9c48a]" : "font-display font-black"} ${accent ? "text-[#d99036]" : ""}`}
      >
        {text}
      </motion.span>
    </span>
  );
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const opacity = useTransform(scrollYProgress, [0, 0.9], [0.35, 0]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative isolate min-h-[100svh] w-full overflow-hidden bg-[#0a0705]"
    >
      {/* Parallax spotlight image */}
      <motion.div
        aria-hidden
        style={{ y, scale, opacity }}
        className="absolute inset-0 -z-10"
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1764850810360-575fdc6815d2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwzfHxjaW5lbWF0aWMlMjB3YXJtJTIwc3BvdGxpZ2h0JTIwYWJzdHJhY3R8ZW58MHx8fHwxNzg0MjI1NzM5fDA&ixlib=rb-4.1.0&q=85')",
          }}
        />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_35%,rgba(217,144,54,0.28),transparent_60%)]" />
        <div className="absolute inset-0 bg-[#0a0705]/70" />
      </motion.div>

      {/* Content */}
      <div className="relative z-10 mx-auto flex min-h-[100svh] max-w-[1400px] flex-col justify-end px-6 pb-24 pt-40 md:px-10 md:pb-32">
        <div className="max-w-[1200px]">
          <h1
            data-testid="hero-headline"
            className="font-display text-[13vw] leading-[0.92] tracking-[-0.045em] text-[#f9f6f0] md:text-[9.2vw] lg:text-[8.2vw]"
          >
            <MaskedLine text={line1} delay={0.15} />
            <MaskedLine text={line2} delay={0.28} italic />
            <MaskedLine text={line3} delay={0.42} />
            <MaskedLine text={line4} delay={0.56} accent />
          </h1>
        </div>

        <div className="mt-14 flex flex-col items-start justify-between gap-10 md:mt-16 md:flex-row md:items-end">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9, ease: easeOut }}
            className="font-body max-w-md text-base leading-relaxed text-[#a89f91] md:text-lg"
          >
            A full-service creative house building brands that last.
            <br />
            <span className="font-serif-editorial italic text-[#e9c48a]">
              Crafted with Trust. Designed for Legacy.
            </span>
          </motion.p>

          <motion.a
            href="#contact"
            data-testid="hero-cta"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.05, ease: easeOut }}
            className="group relative inline-flex items-center gap-4 rounded-full border border-[#d99036]/50 bg-[#d99036]/10 py-5 pl-7 pr-3 backdrop-blur-sm transition-colors duration-500 hover:border-[#d99036] hover:bg-[#d99036]/20"
          >
            <span className="font-display text-sm uppercase tracking-[0.28em] text-[#f9f6f0]">
              Book a Discovery Call
            </span>
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#d99036] text-[#0a0705] transition-transform duration-500 group-hover:rotate-45">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <path d="M7 17L17 7M17 7H8M17 7V16" />
              </svg>
            </span>
          </motion.a>
        </div>
      </div>

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.2, delay: 1.6 }}
        className="absolute bottom-6 right-6 z-10 hidden items-center gap-3 text-[#a89f91] md:right-10 md:flex"
      >
        <span className="overline">Scroll</span>
        <span className="relative block h-10 w-px overflow-hidden bg-[#2a1f17]">
          <motion.span
            animate={{ y: [-40, 40] }}
            transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
            className="absolute inset-x-0 top-0 h-4 bg-[#d99036]"
          />
        </span>
      </motion.div>
    </section>
  );
}
